import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Mail, BookOpen, Archive, ArrowRight, Eye } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName]   = useState("");
  const [status, setStatus] = useState(null); // null | "success" | "error"
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/mailing-list/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim() }),
      });
      if (res.ok) {
        setStatus("success");
        setEmail("");
        setName("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F5F0E8]">
      <SEO
        title="The Dispatch — Donald S. Inbody"
        description="Subscribe to Donald Inbody's newsletter for updates on The Morse Chronicles, naval history research, and new writing."
        canonical="https://inbody.net/newsletter"
        keywords="Donald Inbody newsletter, Morse Chronicles updates, naval fiction newsletter"
      />
      <Navbar />

      {/* Header */}
      <section className="relative pt-32 pb-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 30px, rgba(197,160,89,0.4) 30px, rgba(197,160,89,0.4) 31px)" }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <p className="overline text-[#C5A059] mb-3">Stay Connected</p>
          <h1 data-testid="newsletter-page-title" className="font-serif text-5xl font-black text-[#F5F0E8] mb-2">
            The Dispatch
          </h1>
          <p className="text-[#C5A059]/70 text-sm font-mono tracking-widest mb-4">No. 26-003 — 26 June 2026</p>
          <p className="text-[#F5F0E8]/60 max-w-xl leading-relaxed">
            Occasional updates on my writing, research, new blog posts, and happenings from the shack of ADØK.
          </p>
        </div>
      </section>

      {/* ── Main Content: 2/3 issues + 1/3 subscribe ── */}
      <section className="py-16 bg-[#F5F0E8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">

            {/* ── LEFT 2/3: Newsletter issues ── */}
            <div className="w-full lg:w-2/3 space-y-12">

              {/* Current Issue */}
              <div data-testid="newsletter-current-section">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px flex-1 bg-[#0A1628]/15" />
                  <p className="overline">Current Issue</p>
                  <div className="h-px flex-1 bg-[#0A1628]/15" />
                </div>

                <article data-testid="newsletter-issue-26-003" className="border border-[#0A1628]/12 bg-[#F5F0E8]">
                  <div className="bg-[#0A1628] px-8 py-6 flex items-center justify-between">
                    <div>
                      <p className="text-[#C5A059] text-xs font-bold uppercase tracking-[0.3em] mb-1">The Dispatch</p>
                      <h2 className="font-serif text-2xl font-black text-[#F5F0E8]">No. 26-003 — 26 June 2026</h2>
                    </div>
                    <div className="text-right">
                      <p className="text-[#F5F0E8]/40 text-xs uppercase tracking-widest">Don Inbody</p>
                      <p className="text-[#F5F0E8]/40 text-xs">inbody.net</p>
                    </div>
                  </div>
                  <div className="px-8 py-10 space-y-10 font-serif">
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-4 font-sans">A Writing Update and a New Paper on Mail Voting</h3>
                      <p className="text-[#2D3142] leading-relaxed">
                        A few notes from the desk.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-4 font-sans">The Next Book</h3>
                      <p className="text-[#2D3142] leading-relaxed">
                        I am at work on a follow-on to <em>The Soldier Vote</em>, a history of military and absentee voting in America. It carries the argument of the first book forward and brings it up to the present fight over voting by mail. More as the manuscript takes shape.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-4 font-sans">Wake of the Falcon</h3>
                      <p className="text-[#2D3142] leading-relaxed">
                        My first novel is in the query phase, and I am looking for representation. It is a historical naval thriller set in the Dutch East Indies in August 1914, built around a wireless direction-finding network of expatriate amateur operators. I will post news here as the campaign develops.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-4 font-sans">A New Paper</h3>
                      <p className="text-[#2D3142] leading-relaxed">
                        I have a new paper with the US Vote Foundation, <em>Marked and Missed</em>, on the problems mail voting creates for overseas civilians and military personnel. The federal statute transmits a blank ballot forty-five days before a federal election. The marked ballot then has to travel back, from a ship at sea or a forward base, to a county courthouse in the United States, inside the window state law allows. The mail does not move as fast as the law assumes, and late arrival is the most common reason a military or overseas ballot is rejected. The paper sets out the transit gaps, the state grace periods that close them, and what the current litigation before the Supreme Court would do to both. It is posted in the{" "}
                        <a href="/papers" className="text-[#0A1628] font-semibold underline underline-offset-2 hover:text-[#C5A059] transition-colors">Papers section</a>{" "}
                        of this site.
                      </p>
                    </div>
                    <p className="text-[#2D3142]/60 text-sm border-t border-[#0A1628]/10 pt-6">
                      Thanks for reading.<br />
                      <span className="font-semibold text-[#0A1628]">— Don</span>
                    </p>
                    <div className="flex items-center gap-1.5 text-[#2D3142]/35 text-xs pt-2">
                      <Eye size={11} />
                      <span data-testid="newsletter-reads-26-003">1 read</span>
                    </div>
                  </div>
                </article>
              </div>

              {/* Archive */}
              <div data-testid="newsletter-archive-section">
                <div className="flex items-center gap-4 mb-8">
                  <div className="h-px flex-1 bg-[#0A1628]/15" />
                  <p className="overline"><Archive size={12} className="inline mr-1" />Archive</p>
                  <div className="h-px flex-1 bg-[#0A1628]/15" />
                </div>

                <article data-testid="newsletter-issue-26-002" className="border border-[#0A1628]/12 bg-white mb-8">
                  <div className="bg-[#0A1628] px-8 py-6 flex items-center justify-between">
                    <div>
                      <p className="text-[#C5A059] text-xs font-bold uppercase tracking-[0.3em] mb-1">The Dispatch</p>
                      <h2 className="font-serif text-xl font-black text-[#F5F0E8]">No. 26-002 — 07 May 2026</h2>
                    </div>
                    <div className="text-right">
                      <p className="text-[#F5F0E8]/40 text-xs uppercase tracking-widest">Don Inbody</p>
                      <p className="text-[#F5F0E8]/40 text-xs">inbody.net</p>
                    </div>
                  </div>
                  <div className="px-8 py-8 space-y-8 font-serif">
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">From the Captain's Desk</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        A bit of a rework on the website. Check out the changes — Books, Radio, and here on Dispatch. I also have some new blog posts up.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">What I'm Working On</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        No changes here. <em>Wake of the Falcon</em> is in its final stages. I have a couple of beta readers helping me out. Good feedback so far.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">On the Air</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        Nothing new on the air per-se, but the Radio page is re-worked. You can see if I am on the air and what frequency and mode I am operating on. I also have my last 10 QSOs (Club Log) showing as well as a neat new solar conditions and propagation indicator.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">My Recommendation</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        Still enjoying Rhiannon Giddens. You can catch her{" "}
                        <a href="https://www.pbs.org/show/my-music-rhiannon-giddens/" target="_blank" rel="noopener noreferrer"
                          className="text-[#0A1628] font-semibold underline underline-offset-2 hover:text-[#C5A059] transition-colors">
                          My Music series on PBS
                        </a>.
                      </p>
                    </div>
                    <p className="text-[#2D3142]/50 text-xs border-t border-[#0A1628]/10 pt-5">
                      Thanks for reading. Sign up for the newsletter notifications and get on my mailing list.<br />
                      <span className="font-semibold text-[#0A1628]">— Don</span>
                    </p>
                    <div className="flex items-center gap-1.5 text-[#2D3142]/35 text-xs pt-2">
                      <Eye size={11} />
                      <span data-testid="newsletter-reads-26-002">11 reads</span>
                    </div>
                  </div>
                </article>

                <article data-testid="newsletter-issue-26-001" className="border border-[#0A1628]/12 bg-white">
                  <div className="bg-[#0A1628] px-8 py-6 flex items-center justify-between">
                    <div>
                      <p className="text-[#C5A059] text-xs font-bold uppercase tracking-[0.3em] mb-1">The Dispatch</p>
                      <h2 className="font-serif text-xl font-black text-[#F5F0E8]">No. 26-001 — 05 May 2026</h2>
                    </div>
                    <div className="text-right">
                      <p className="text-[#F5F0E8]/40 text-xs uppercase tracking-widest">Don Inbody</p>
                      <p className="text-[#F5F0E8]/40 text-xs">inbody.net</p>
                    </div>
                  </div>
                  <div className="px-8 py-8 space-y-8 font-serif">
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">From the Captain's Desk</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        May arrived with a full to-do list and grass that doesn't care. Four acres doesn't mow itself, and when the zero-turn shed its hydro belt, I lost two days to figuring out why — and another half-day getting it back together. That's done now. First full mow of the season is behind me. Weed-whacking is not.
                      </p>
                      <p className="text-[#2D3142] leading-relaxed text-sm mt-3">
                        In between, we got to Bass Auditorium last Saturday for Rhiannon Giddens. If you don't know her work, fix that. She moves between traditions — old-time, folk, classical — like they were never separate to begin with. Remarkable show. Tuesday is our anniversary, and we have tickets to see Sting. Not a bad week.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">What I'm Working On</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        <em>Wake of the Falcon</em> is in final cleanup. I just reworked the climax and I'm satisfied with where it landed. The target for agent submissions is June or July. I have two pieces out with editors — one at <em>Joint Forces Quarterly</em>, one a series of three articles submitted to <em>QST</em> — and I'm waiting on both. Waiting is most of this business.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">On the Air</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        New hardline is run to the tower and the Mosley beam. The station is tighter than it's been. The 80 and 160 meter wire antennas are still not up — that project keeps sliding — but the HF capability I do have is working cleanly. Progress is progress.
                      </p>
                    </div>
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5A059] mb-3 font-sans">My Recommendation</h3>
                      <p className="text-[#2D3142] leading-relaxed text-sm">
                        <a href="https://rhiannongiddens.com" target="_blank" rel="noopener noreferrer"
                          className="text-[#0A1628] font-semibold underline underline-offset-2 hover:text-[#C5A059] transition-colors">
                          Rhiannon Giddens
                        </a>. Start with <em>You're the One</em> or her work with Francesco Turrisi. Then go back to the Carolina Chocolate Drops years. You'll lose an afternoon.
                      </p>
                    </div>
                    <p className="text-[#2D3142]/50 text-xs border-t border-[#0A1628]/10 pt-5">
                      If the agents respond, you'll hear about it. If the grass wins, you'll also hear about it. Either way — thanks for reading.{" "}
                      <span className="font-semibold text-[#0A1628]">— Don</span>
                    </p>
                    {/* Read count */}
                    <div className="flex items-center gap-1.5 text-[#2D3142]/35 text-xs pt-2">
                      <Eye size={11} />
                      <span data-testid="newsletter-reads-26-001">7 reads</span>
                    </div>
                  </div>
                </article>
              </div>

            </div>{/* end left */}

            {/* ── RIGHT 1/3: Subscribe (sticky) ── */}
            <div className="w-full lg:w-1/3 lg:sticky lg:top-8">
              <div className="bg-white border border-[#0A1628]/10 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 bg-[#0A1628] flex items-center justify-center flex-shrink-0">
                    <Mail size={15} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <h2 className="font-serif text-lg font-bold text-[#0A1628]">Subscribe to The Dispatch</h2>
                    <p className="text-[#2D3142]/60 text-xs">No spam. Unsubscribe any time.</p>
                  </div>
                </div>

                {status === "success" ? (
                  <div data-testid="subscribe-success" className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
                    You're subscribed. Thank you — you'll hear from me when the next issue goes out.
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="space-y-3">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-1.5 block">Name (optional)</label>
                      <input
                        type="text"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Your name"
                        data-testid="newsletter-name-input"
                        className="w-full border border-[#0A1628]/20 bg-[#F5F0E8] px-3 py-2 text-sm text-[#0A1628] focus:outline-none focus:border-[#0A1628]"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-[#2D3142]/60 mb-1.5 block">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="your@email.com"
                        required
                        data-testid="newsletter-email-input"
                        className="w-full border border-[#0A1628]/20 bg-[#F5F0E8] px-3 py-2 text-sm text-[#0A1628] focus:outline-none focus:border-[#0A1628]"
                      />
                    </div>
                    {status === "error" && (
                      <p className="text-red-600 text-xs">Something went wrong. Please try again.</p>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="newsletter-subscribe-btn"
                      className="w-full flex items-center justify-center gap-2 bg-[#0A1628] text-[#F5F0E8] px-4 py-2.5 text-sm font-semibold hover:bg-[#2D3142] transition-colors disabled:opacity-50"
                    >
                      {submitting ? "Subscribing…" : <><Mail size={13} /> Subscribe</>}
                    </button>
                  </form>
                )}
              </div>
            </div>{/* end right */}

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
